import intervalToDuration from 'date-fns/intervalToDuration';
import formatISO from 'date-fns/formatISO';

import {
    getEventPageUrl,
    getCanonicalEventPageUrl,
    eventHasCustomDomain,
} from 'common/components/TouchpointEditor/utils';
import { VIRTUAL_ATTENDANCE_TYPE } from 'common/schemas/session';
import { getVirtualSessionsApiClient } from 'lib/SplashApiClient/subclients/VirtualSessionsApiClient';
import { downloadDataBlob } from 'common/utils/downloadDataBlob';

import Google from 'common/components/TouchpointEditor/components/TouchpointNodes/CalendarAttachment/Icons/Google';
import Outlook from 'common/components/TouchpointEditor/components/TouchpointNodes/CalendarAttachment/Icons/Outlook';
import Apple from 'common/components/TouchpointEditor/components/TouchpointNodes/CalendarAttachment/Icons/Apple';
import Yahoo from 'common/components/TouchpointEditor/components/TouchpointNodes/CalendarAttachment/Icons/Yahoo';

export const MAX_DESCRIPTION_LENGTH = 850;

/**
 * Returns a time string with a leading zero if the time is less than 10.
 * @param time
 * @returns {string}
 */
function paddedTimeString(time = 0) {
    return `0${time}`.slice(-2);
}

function calculateDateStrings(start, end) {
    let sessionStartDate = '';
    let sessionDuration = '';
    let sessionDatesString = '';
    if (start) {
        const startDate = new Date(start);
        sessionStartDate = start ? formatISO(startDate, { format: 'basic' }) : '';

        if (end) {
            const endDate = new Date(end);
            const sessionEndDate = end ? formatISO(endDate, { format: 'basic' }) : '';
            const { hours, minutes } = intervalToDuration({
                start: startDate,
                end: endDate,
            });
            const hoursString = paddedTimeString(hours);
            const minutesString = paddedTimeString(minutes);
            sessionDuration = `${hoursString}${minutesString}`;
            const sessionEndDateString = sessionEndDate ? `/${sessionEndDate}` : '';
            sessionDatesString = `${sessionStartDate}${sessionEndDateString}`;
        }
    }
    return { sessionStartDate, sessionDuration, sessionDatesString };
}

export const getAgendaCalendarProviders = ({ session, eventId, apiUrl, guestAccessHash }) => {
    const virtualSessionsApiClient = getVirtualSessionsApiClient();
    const sessionTitle = session?.name || '';
    const sessionDescription = session?.description || '';
    // TODO Find out if this info exists for sessions
    const sessionLocation = session?.location || '';
    const { sessionStartDate, sessionDuration, sessionDatesString } = calculateDateStrings(
        session?.start,
        session?.end,
    );

    const isVirtualSession = session?.types?.includes(VIRTUAL_ATTENDANCE_TYPE);
    const eventUrl = eventHasCustomDomain() ? getCanonicalEventPageUrl() : getEventPageUrl();
    const virtualEventUrl = `${eventUrl}/virtual/session/${session?.id}?guest-access-hash=${guestAccessHash}`;

    const calendarDescription = isVirtualSession
        ? `Join URL: ${virtualEventUrl}\n\n${sessionDescription}`
        : sessionDescription;

    const virtualSessionCalendarDescriptionPrefix = encodeURI('Join URL: ');
    const encodedSessionLocation = encodeURIComponent(sessionLocation);
    const encodedSessionTitle = encodeURIComponent(sessionTitle);
    const encodedSessionDescription = encodeURIComponent(sessionDescription);
    const encodedCalendarDescription = isVirtualSession
        ? `${virtualSessionCalendarDescriptionPrefix}${virtualEventUrl}%0A%0A${encodedSessionDescription}`
        : encodedSessionDescription;
    const encodedCalendarDescriptionAdjustedLength = encodedCalendarDescription.slice(0, MAX_DESCRIPTION_LENGTH);

    const downloadCalendarAttachment = () => async () => {
        const calendarAttachment = await virtualSessionsApiClient.getSessionCalendarAttachment({
            eventId,
            sessionId: session.id,
            guestAccessHash,
        });
        downloadDataBlob(calendarAttachment, 'calendar.ics', 'text/calendar; charset=utf-8');
    };

    return [
        {
            label: 'Google',
            provider: 'Gmail',
            icon: Google,
            getLink: () =>
                `http://www.google.com/calendar/event?action=TEMPLATE&text=${encodedSessionTitle}&dates=${sessionDatesString}&details=${encodedCalendarDescriptionAdjustedLength}&location=${encodedSessionLocation}`,
        },
        {
            label: 'Outlook',
            provider: 'Outlook',
            icon: Outlook,
            getLink: () => {
                const url = new URLSearchParams();

                url.append('subject', sessionTitle);
                url.append('location', sessionLocation);
                url.append('body', calendarDescription.replace('\n\n', '<br/>'));
                url.append('startdt', session?.start);
                url.append('enddt', session?.end);
                url.append('path', 'calendar/action/compose');
                url.append('rru', 'addevent');

                return `https://outlook.office365.com/calendar/action/compose?${url}`;
            },
        },
        {
            label: 'Apple',
            provider: 'iCal',
            icon: Apple,
            getLink: downloadCalendarAttachment,
        },
        {
            label: 'Yahoo',
            provider: 'Yahoo',
            icon: Yahoo,
            getLink: () =>
                `http://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodedSessionTitle}&st=${sessionStartDate}&desc=${encodedCalendarDescriptionAdjustedLength}&in_loc=${encodedSessionLocation}&dur=${sessionDuration}`,
        },
    ];
};
